import { useEffect, useState, useCallback, useRef } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { toast } from "sonner";
import {
  CalendarClock, Plus, Pencil, Trash2, CheckCircle2, AlertTriangle, Loader2,
  Paperclip, X, History, Sparkles, FileCheck2, BellRing, Send, Building2, Phone, Mail, Hash, User, Users, SlidersHorizontal,
  ChevronDown, ChevronLeft, ChevronRight, Eye,
} from "lucide-react";
import { RenewalDetailSheet } from "@/components/RenewalDetailSheet";
import { NotifyPicker } from "@/components/renewals/NotifyPicker";
import { STATUS_META, RECURRENCE_LABELS, OVERDUE_CHOICES, overdueLabel, vendorText, nextReminderText, when, notifyLabel, CHAN_WORD } from "./renewalsMeta";
import { V } from "@/lib/vocab";

const inputCls = "w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";
const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-sm font-semibold">{label}{hint && <span className="font-normal text-muted-foreground"> — {hint}</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
const EMPTY_VENDOR = { company: "", contact_name: "", phone: "", email: "", account_number: "" };
// New items follow the community default cadence until the manager overrides it.
const blank = (cats, defaults) => ({
  title: "", category: cats?.[0]?.key || "permit", due_on: "", recurrence: "annual",
  owner_user_id: "", notes: "", vendor: { ...EMPTY_VENDOR }, cost: "",
  use_default_reminders: true, lead_days: defaults?.lead_days || [90, 60, 30, 7],
  overdue_every_days: defaults?.overdue_every_days ?? 7,
  notify_mode: "default", notify_user_ids: [], notify_emails: [],
});
const LEAD_CHOICES = [180, 120, 90, 60, 45, 30, 14, 7, 3, 1];
const GROUP_PAGE = 10;   // register rows per page inside each status group

export default function Renewals() {
  const { tenantId, role } = useDash();
  const readOnly = role === "board_member";
  const [data, setData] = useCachedState(`renewals:${tenantId}`, null);
  const [edit, setEdit] = useState(null);         // item being created/edited
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(null);   // item being filed
  const [proofFile, setProofFile] = useState(null);
  const [completeNote, setCompleteNote] = useState("");
  const [openHistory, setOpenHistory] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);   // community default cadence being edited
  const [detailId, setDetailId] = useState(null);   // item open in the detail sheet
  const [groupPage, setGroupPage] = useState({});   // per-status-group page index
  const [suggestOpen, setSuggestOpen] = useState(false);
  const proofRef = useRef(null);
  // A bell alert or a reminder email deep-links to one obligation — bring it into view and
  // ring it, so nobody has to hunt down the row they were just told about.
  const [params] = useSearchParams();
  const focusId = params.get("item");
  useEffect(() => {
    if (!focusId || !data) return;
    const el = document.querySelector(`[data-testid="renewal-${focusId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (data.items?.some((i) => i.id === focusId)) setDetailId(focusId);
  }, [focusId, data]);
  // Suggestions start collapsed once the register has rows; an empty register wants them open.
  useEffect(() => { if (data) setSuggestOpen((data.items || []).length === 0); }, [data?.items?.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // iter384 — "Track this in Renewals" from an expiring document pre-fills a new item.
  useEffect(() => {
    if (!data || params.get("new") !== "1") return;
    const cats = data.categories || [];
    const want = params.get("category") || "";
    setEdit({ ...blank(cats, data.reminder_settings), title: params.get("title") || "", due_on: params.get("due") || "",
      category: cats.some((c) => c.key === want) ? want : (cats[0]?.key || "permit") });
  }, [data, params]);

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/renewals`)
      .then((r) => {
        setData(r.data);
        // Keep the sidebar's rose badge honest while the manager works this page.
        window.dispatchEvent(new CustomEvent("redline:renewals-overdue", {
          detail: { tenantId, count: r.data?.counts?.overdue || 0 } }));
      })
      .catch(() => toast.error("Couldn't load your renewals"));
  }, [tenantId, setData]);
  useEffect(() => { if (tenantId) load(); }, [tenantId, load]);

  const save = async () => {
    if (!edit.title.trim()) { toast.error("Give it a name"); return; }
    if (!edit.due_on) { toast.error("Pick the date it's due"); return; }
    setBusy(true);
    try {
      const useDefault = !!edit.use_default_reminders;
      const body = {
        title: edit.title.trim(), category: edit.category, due_on: edit.due_on,
        recurrence: edit.recurrence, owner_user_id: edit.owner_user_id || null,
        notes: edit.notes || "", vendor: { ...EMPTY_VENDOR, ...(edit.vendor || {}) },
        cost: edit.cost === "" ? null : Number(edit.cost),
        use_default_reminders: useDefault,
        lead_days: useDefault ? [] : (edit.lead_days || []),
        overdue_every_days: useDefault ? null : Number(edit.overdue_every_days ?? 7),
        notify_mode: edit.notify_mode || "default",
        notify_user_ids: edit.notify_user_ids || [],
        notify_emails: edit.notify_emails || [],
      };
      if (edit.id) await api.put(`/tenants/${tenantId}/renewals/${edit.id}`, body);
      else await api.post(`/tenants/${tenantId}/renewals${edit.source_key ? `?source_key=${edit.source_key}` : ""}`, body);
      const leads = [...(useDefault ? (data.reminder_settings?.lead_days || [90, 60, 30, 7]) : (edit.lead_days?.length ? edit.lead_days : (data.reminder_settings?.lead_days || [90, 60, 30, 7])))].sort((a, b) => b - a);
      toast.success(edit.id ? "Renewal updated" : `Tracking it — reminders at ${leads.join(", ")} days out`);
      setEdit(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save that"); }
    finally { setBusy(false); }
  };

  const openSettings = () => { setSettings({ ...(data.reminder_settings || { lead_days: [90, 60, 30, 7], overdue_every_days: 7 }) }); setSettingsOpen(true); };
  const saveSettings = async () => {
    if (!settings.lead_days?.length) { toast.error("Pick at least one lead time"); return; }
    setBusy(true);
    try {
      const r = await api.put(`/tenants/${tenantId}/renewals/settings`, settings);
      toast.success(`${V.one("org")} default saved — ${[...r.data.lead_days].sort((a, b) => b - a).join(", ")} days out, overdue ${overdueLabel(r.data.overdue_every_days)}`);
      setSettingsOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save that"); }
    finally { setBusy(false); }
  };

  const sendTest = async (item) => {
    try {
      const r = await api.post(`/tenants/${tenantId}/renewals/${item.id}/test-reminder`);
      toast.success(`Test reminder sent to you (${r.data.channels.join(" + ")}) — check the bell${r.data.channels.includes("email") ? " and your inbox" : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send the test"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Stop tracking "${item.title}"? Its history goes too.`)) return;
    try {
      await api.delete(`/tenants/${tenantId}/renewals/${item.id}`);
      toast.success("Removed from the register");
      load();
    } catch { toast.error("Couldn't remove that"); }
  };

  const fileCycle = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("note", completeNote || "");
      if (proofFile) fd.append("file", proofFile, proofFile.name);
      const r = await api.post(`/tenants/${tenantId}/renewals/${completing.id}/complete`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data?.due_on
        ? `Filed — next one scheduled for ${r.data.due_on}`
        : "Filed and closed out");
      setCompleting(null); setProofFile(null); setCompleteNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't file that"); }
    finally { setBusy(false); }
  };

  const startEdit = (i) => setEdit({ ...i, cost: i.cost ?? "", vendor: { ...EMPTY_VENDOR, ...(i.vendor || {}), company: i.vendor?.company || i.vendor_name || "" }, use_default_reminders: !!i.uses_default_reminders, lead_days: i.effective_lead_days || i.lead_days || [90, 60, 30, 7], overdue_every_days: i.effective_overdue_every_days ?? 7, notify_mode: i.notify_mode || "default", notify_user_ids: i.notify_user_ids || [], notify_emails: i.notify_emails || [] });
  const startComplete = (i) => { setCompleting(i); setProofFile(null); setCompleteNote(""); };
  const detailItem = detailId ? (data?.items || []).find((i) => i.id === detailId) : null;

  const accept = (s) => setEdit({
    ...blank(data.categories, data.reminder_settings), title: s.title, category: s.category,
    recurrence: s.recurrence, notes: s.notes || "", source_key: s.key,
  });

  const dismiss = async (s) => {
    try {
      await api.post(`/tenants/${tenantId}/renewals/suggestions/dismiss`, { key: s.key });
      setData((d) => ({ ...d, suggestions: d.suggestions.filter((x) => x.key !== s.key) }));
    } catch { toast.error("Couldn't dismiss that"); }
  };

  if (!data) {
    return <div className="flex items-center gap-2 text-muted-foreground p-6" data-testid="renewals-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading renewals…</div>;
  }

  const c = data.counts || {};
  const groups = [
    ["overdue", data.items.filter((i) => i.status === "overdue")],
    ["due_soon", data.items.filter((i) => i.status === "due_soon")],
    ["upcoming", data.items.filter((i) => i.status === "upcoming")],
  ];

  return (
    <div className="space-y-6" data-testid="renewals-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <PageTitle title={<><CalendarClock className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Renewals</>} about={<>Insurance, permits, inspections, audits and filings — the deadlines nobody else reminds you about.
            Mark one renewed with the certificate and the next cycle schedules itself.</>} />
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button data-testid="renewal-settings-btn" onClick={openSettings} title={`${V.one("org")} default: how early and how often reminders go out`}
              className="rounded-full border border-sand-200 bg-white text-ink font-semibold px-4 py-2.5 flex items-center gap-2 hover:border-ink transition-colors text-sm">
              <SlidersHorizontal className="h-4 w-4" /> Reminder defaults
            </button>
            <button data-testid="renewal-add-btn" onClick={() => setEdit(blank(data.categories, data.reminder_settings))}
              className="rounded-full bg-redline-500 text-white font-semibold px-5 py-2.5 flex items-center gap-2 hover:bg-redline-600 transition-colors">
              <Plus className="h-4 w-4" /> Track something
            </button>
          </div>
        )}
      </div>

      {/* Community default cadence — every item without its own override follows this. */}
      {settingsOpen && settings && (
        <section className="bg-white rounded-2xl border-2 border-sand-300 p-5 space-y-3" data-testid="renewal-settings-panel">
          <div>
            <h2 className="font-heading font-bold text-ink flex items-center gap-2"><BellRing className="h-4 w-4 text-redline-500" /> Reminder defaults for {`this ${V.lc("org")}`}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">How early reminders start and, once something is overdue, how often the nag repeats. Any obligation can override this on its own card.</p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-ink/60 mb-1.5">How early — days before the due date</div>
            <div className="flex flex-wrap gap-1.5" data-testid="renewal-settings-lead-days">
              {LEAD_CHOICES.map((d) => {
                const on = (settings.lead_days || []).includes(d);
                return (
                  <button key={d} type="button" data-testid={`renewal-settings-lead-${d}`} aria-pressed={on}
                    onClick={() => setSettings((st) => { const cur = new Set(st.lead_days || []); if (cur.has(d)) cur.delete(d); else cur.add(d); return { ...st, lead_days: [...cur].sort((a, b) => b - a) }; })}
                    className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${on ? "border-ink bg-ink text-white" : "border-sand-300 bg-white text-ink/60 hover:border-ink/40"}`}>
                    {d}d
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-ink/60 mb-1.5">How often once overdue</div>
            <div className="flex flex-wrap gap-1.5" data-testid="renewal-settings-overdue">
              {OVERDUE_CHOICES.map(([v, label]) => (
                <button key={v} type="button" data-testid={`renewal-settings-overdue-${v}`} aria-pressed={Number(settings.overdue_every_days) === v}
                  onClick={() => setSettings((st) => ({ ...st, overdue_every_days: v }))}
                  className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${Number(settings.overdue_every_days) === v ? "border-ink bg-ink text-white" : "border-sand-300 bg-white text-ink/60 hover:border-ink/40"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-ink/70" data-testid="renewal-settings-summary">
            Reminders at <b>{[...(settings.lead_days || [])].sort((a, b) => b - a).join(", ") || "—"} days</b> before the due date; once overdue, <b>{overdueLabel(settings.overdue_every_days)}</b> until it&apos;s filed. Applies to <b>{data.items.filter((i) => i.uses_default_reminders).length}</b> of {data.items.length} tracked obligation{data.items.length === 1 ? "" : "s"}.
          </p>
          <div className="flex items-center gap-2">
            <button data-testid="renewal-settings-save" disabled={busy} onClick={saveSettings}
              className="rounded-full bg-ink text-white font-semibold px-5 py-2 text-sm hover:bg-ink-700 disabled:opacity-60">{busy ? "Saving…" : "Save defaults"}</button>
            <button data-testid="renewal-settings-cancel" onClick={() => setSettingsOpen(false)} className="text-sm font-semibold text-muted-foreground hover:text-ink">Cancel</button>
          </div>
        </section>
      )}

      {/* counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="renewals-counts">
        {[["Tracked", c.total, "text-ink"], ["Overdue", c.overdue, "text-rose-600"],
          ["Due within 30 days", c.due_soon, "text-amber-600"], ["Upcoming", c.upcoming, "text-ink/70"]].map(([label, n, cls]) => (
          <div key={label} className="bg-white rounded-2xl border border-sand-200 p-4">
            <div className={`font-heading text-2xl font-extrabold ${cls}`}>{n ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* editor */}
      {edit && (
        <section className="bg-white rounded-2xl border-2 border-redline-200 p-5 space-y-4" data-testid="renewal-editor">
          <h2 className="font-heading font-bold text-ink">{edit.id ? "Edit obligation" : "Track a new obligation"}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="What is it?">
              <input data-testid="renewal-title" autoFocus value={edit.title}
                onChange={(e) => setEdit((s) => ({ ...s, title: e.target.value }))}
                className={inputCls} placeholder="Pool operating permit renewal" />
            </Field>
            <Field label="Type">
              <select data-testid="renewal-category" value={edit.category}
                onChange={(e) => setEdit((s) => ({ ...s, category: e.target.value }))} className={inputCls}>
                {data.categories.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </Field>
            <Field label="Due date">
              <input data-testid="renewal-due" type="date" value={edit.due_on}
                onChange={(e) => setEdit((s) => ({ ...s, due_on: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="How often">
              <select data-testid="renewal-recurrence" value={edit.recurrence}
                onChange={(e) => setEdit((s) => ({ ...s, recurrence: e.target.value }))} className={inputCls}>
                {data.recurrences.map((r) => <option key={r} value={r}>{RECURRENCE_LABELS[r] || r}</option>)}
              </select>
            </Field>
            <Field label="Who owns it" hint="they get the nudges">
              <select data-testid="renewal-owner" value={edit.owner_user_id || ""}
                onChange={(e) => setEdit((s) => ({ ...s, owner_user_id: e.target.value }))} className={inputCls}>
                <option value="">The whole management team</option>
                {data.owners.map((o) => <option key={o.user_id} value={o.user_id}>{o.name}</option>)}
              </select>
            </Field>
            <Field label="Cost" hint="optional">
              <input data-testid="renewal-cost" type="number" min="0" step="0.01" value={edit.cost ?? ""}
                onChange={(e) => setEdit((s) => ({ ...s, cost: e.target.value }))} className={inputCls} />
            </Field>
          </div>
          <NotifyPicker value={edit} people={data.notify_people || []} maxEmails={data.max_notify_emails ?? 10}
            ownerUserId={edit.owner_user_id || ""} ownerName={data.owners.find((o) => o.user_id === edit.owner_user_id)?.name}
            onChange={(patch) => setEdit((s) => ({ ...s, ...patch }))} />
          {/* Third-party vendor — who to call when the reminder lands. Rides along in every reminder. */}
          <div data-testid="renewal-vendor-block" className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-ink/60"><Building2 className="h-3.5 w-3.5" /> Vendor / third party <span className="font-normal normal-case text-ink/50">— optional, included in every reminder</span></div>
            <div className="grid md:grid-cols-2 gap-2">
              <input data-testid="renewal-vendor" placeholder="Company (e.g. Acme Insurance)" value={edit.vendor?.company || ""}
                onChange={(e) => setEdit((s) => ({ ...s, vendor: { ...EMPTY_VENDOR, ...(s.vendor || {}), company: e.target.value } }))} className={inputCls} />
              <input data-testid="renewal-vendor-contact" placeholder="Contact person" value={edit.vendor?.contact_name || ""}
                onChange={(e) => setEdit((s) => ({ ...s, vendor: { ...EMPTY_VENDOR, ...(s.vendor || {}), contact_name: e.target.value } }))} className={inputCls} />
              <input data-testid="renewal-vendor-phone" placeholder="Phone" inputMode="tel" value={edit.vendor?.phone || ""}
                onChange={(e) => setEdit((s) => ({ ...s, vendor: { ...EMPTY_VENDOR, ...(s.vendor || {}), phone: e.target.value } }))} className={inputCls} />
              <input data-testid="renewal-vendor-email" placeholder="Email" inputMode="email" value={edit.vendor?.email || ""}
                onChange={(e) => setEdit((s) => ({ ...s, vendor: { ...EMPTY_VENDOR, ...(s.vendor || {}), email: e.target.value } }))} className={inputCls} />
              <input data-testid="renewal-vendor-account" placeholder="Account / policy #" value={edit.vendor?.account_number || ""}
                onChange={(e) => setEdit((s) => ({ ...s, vendor: { ...EMPTY_VENDOR, ...(s.vendor || {}), account_number: e.target.value } }))} className={inputCls} />
            </div>
          </div>
          <Field label="Notes" hint="what future-you needs to know">
            <textarea data-testid="renewal-notes" rows={2} value={edit.notes || ""}
              onChange={(e) => setEdit((s) => ({ ...s, notes: e.target.value }))} className={`${inputCls} resize-y`} />
          </Field>
          {/* Reminders — who, when, how. This used to be invisible; the schedule ran anyway. */}
          <div data-testid="renewal-reminders" className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-ink/60"><BellRing className="h-3.5 w-3.5" /> Reminders</div>
              <div className="flex rounded-full border border-sand-300 bg-white p-0.5 text-xs font-semibold" role="tablist">
                <button type="button" data-testid="renewal-reminders-default" aria-pressed={!!edit.use_default_reminders}
                  onClick={() => setEdit((s) => ({ ...s, use_default_reminders: true }))}
                  className={`rounded-full px-3 py-1 ${edit.use_default_reminders ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>{V.one("org")} default</button>
                <button type="button" data-testid="renewal-reminders-custom" aria-pressed={!edit.use_default_reminders}
                  onClick={() => setEdit((s) => ({ ...s, use_default_reminders: false, lead_days: s.lead_days?.length ? s.lead_days : (data.reminder_settings?.lead_days || [90, 60, 30, 7]), overdue_every_days: s.overdue_every_days ?? (data.reminder_settings?.overdue_every_days ?? 7) }))}
                  className={`rounded-full px-3 py-1 ${!edit.use_default_reminders ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>Custom for this one</button>
              </div>
            </div>
            {edit.use_default_reminders && (
              <p className="text-xs text-ink/70" data-testid="renewal-reminders-default-summary">
                Follows the {V.lc("org")} default: <b>{[...(data.reminder_settings?.lead_days || [90, 60, 30, 7])].sort((a, b) => b - a).join(", ")} days</b> before, then <b>{overdueLabel(data.reminder_settings?.overdue_every_days ?? 7)}</b> once overdue. {!readOnly && <button type="button" className="font-semibold text-redline-600 hover:text-redline-700" onClick={openSettings}>Change the default</button>}
              </p>
            )}
            <div className={`flex flex-wrap gap-1.5 ${edit.use_default_reminders ? "hidden" : ""}`} data-testid="renewal-lead-days">
              {LEAD_CHOICES.map((d) => {
                const on = (edit.lead_days || []).includes(d);
                return (
                  <button key={d} type="button" data-testid={`renewal-lead-${d}`} aria-pressed={on}
                    onClick={() => setEdit((s) => {
                      const cur = new Set(s.lead_days || []);
                      if (cur.has(d)) cur.delete(d); else cur.add(d);
                      return { ...s, lead_days: [...cur].sort((a, b) => b - a) };
                    })}
                    className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${on ? "border-ink bg-ink text-white" : "border-sand-300 bg-white text-ink/60 hover:border-ink/40"}`}>
                    {d}d
                  </button>
                );
              })}
            </div>
            {!edit.use_default_reminders && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="renewal-overdue-every">
                <span className="text-xs text-ink/60 mr-1">Once overdue:</span>
                {OVERDUE_CHOICES.map(([v, label]) => (
                  <button key={v} type="button" data-testid={`renewal-overdue-${v}`} aria-pressed={Number(edit.overdue_every_days) === v}
                    onClick={() => setEdit((s) => ({ ...s, overdue_every_days: v }))}
                    className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${Number(edit.overdue_every_days) === v ? "border-ink bg-ink text-white" : "border-sand-300 bg-white text-ink/60 hover:border-ink/40"}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            <p className="text-xs text-ink/70" data-testid="renewal-reminders-summary">
              {(() => {
                const leads = [...((edit.use_default_reminders ? data.reminder_settings?.lead_days : edit.lead_days) || [])].sort((a, b) => b - a);
                const every = edit.use_default_reminders ? (data.reminder_settings?.overdue_every_days ?? 7) : Number(edit.overdue_every_days ?? 7);
                const who = edit.owner_user_id ? (data.owners.find((o) => o.user_id === edit.owner_user_id)?.name || "the owner") : "the whole management team";
                const picked = (edit.notify_user_ids || []).length + (edit.notify_emails || []).length;
                const mode = edit.notify_mode || "default";
                const target = !picked || mode === "default" ? <b>{who}</b>
                  : mode === "only" ? <><b>the {picked} {picked === 1 ? "person" : "people"} you picked</b> above</>
                    : <><b>{who}</b> plus the <b>{picked}</b> you picked above</>;
                return leads.length
                  ? <>A reminder goes to {target} at <b>{leads.join(", ")} days</b> before the due date; if it goes overdue, an alert <b>{every > 0 ? `${overdueLabel(every)} until it's filed` : "once"}</b> — by <b>bell + email</b> (text too, for anyone who turned on texts under Notifications). {edit.owner_user_id || picked ? "" : "Pick an owner above to send them to one person instead."}</>
                  : <>Pick at least one lead time — with none selected the {V.lc("org")} default ({[...(data.reminder_settings?.lead_days || [90, 60, 30, 7])].sort((a, b) => b - a).join(", ")} days) applies.</>;
              })()}
            </p>
            {edit.id && (
              <button type="button" data-testid="renewal-test-reminder" onClick={() => sendTest(edit)}
                className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink">
                <Send className="h-3.5 w-3.5" /> Send me a test reminder
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="renewal-save" disabled={busy} onClick={save}
              className="rounded-full bg-redline-500 text-white font-semibold px-5 py-2 text-sm hover:bg-redline-600 disabled:opacity-60">
              {busy ? "Saving…" : edit.id ? "Save changes" : "Start tracking it"}
            </button>
            <button data-testid="renewal-cancel" onClick={() => setEdit(null)}
              className="text-sm font-semibold text-muted-foreground hover:text-ink">Cancel</button>
          </div>
        </section>
      )}

      {/* the register */}
      {data.items.length === 0 && !edit && (
        <section className="bg-white rounded-2xl border border-sand-200 p-8 text-center" data-testid="renewals-empty">
          <CalendarClock className="h-10 w-10 text-sand-300 mx-auto mb-3" strokeWidth={1.5} />
          <h2 className="font-heading font-bold text-ink">Nothing tracked yet</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Start with the ones that cost real money if they lapse — insurance, the pool permit, the annual audit.
            Pick from the suggestions below or add your own.
          </p>
        </section>
      )}

      {groups.map(([key, allItems]) => allItems.length > 0 && (() => {
        const pages = Math.max(1, Math.ceil(allItems.length / GROUP_PAGE));
        const pg = Math.min(groupPage[key] || 1, pages);
        const items = allItems.slice((pg - 1) * GROUP_PAGE, pg * GROUP_PAGE);
        const pager = pages > 1 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`renewals-pager-${key}`}>
            <span data-testid={`renewals-range-${key}`}>{(pg - 1) * GROUP_PAGE + 1}–{Math.min(pg * GROUP_PAGE, allItems.length)} of {allItems.length}</span>
            <button data-testid={`renewals-prev-${key}`} disabled={pg <= 1} onClick={() => setGroupPage((g) => ({ ...g, [key]: pg - 1 }))}
              className="h-7 w-7 rounded-lg border border-sand-200 bg-white flex items-center justify-center hover:border-ink disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" /></button>
            <span className="tabular-nums" data-testid={`renewals-page-${key}`}>{pg}/{pages}</span>
            <button data-testid={`renewals-next-${key}`} disabled={pg >= pages} onClick={() => setGroupPage((g) => ({ ...g, [key]: pg + 1 }))}
              className="h-7 w-7 rounded-lg border border-sand-200 bg-white flex items-center justify-center hover:border-ink disabled:opacity-40"><ChevronRight className="h-3.5 w-3.5" /></button>
          </div>
        );
        return (
        <section key={key} className="space-y-2" data-testid={`renewals-group-${key}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-heading font-bold text-ink flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${STATUS_META[key].dot}`} />
              {STATUS_META[key].label} <span className="text-muted-foreground font-normal text-sm">({allItems.length})</span>
            </h2>
            {pager}
          </div>
          {items.map((i) => (
            <div key={i.id} className={`bg-white rounded-2xl border p-4 transition-shadow ${focusId === i.id ? "border-redline-400 ring-2 ring-redline-500/25" : "border-sand-200"}`} data-testid={`renewal-${i.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-[14rem] flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button data-testid={`renewal-open-${i.id}`} onClick={() => setDetailId(i.id)} title="See every detail"
                      className="font-semibold text-ink text-left hover:text-redline-600 hover:underline underline-offset-2 transition-colors">{i.title}</button>
                    <span className={`text-[11px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5 ${STATUS_META[i.status].cls}`}
                      data-testid={`renewal-status-${i.id}`}>
                      {STATUS_META[i.status].label}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {i.category_label} · {when(i)} · {RECURRENCE_LABELS[i.recurrence] || i.recurrence}
                    {i.owner_name ? ` · ${i.owner_name}` : " · whole team"}
                    {i.cost ? ` · $${i.cost.toLocaleString()}` : ""}
                  </div>
                  {vendorText(i.vendor) && (
                    <div className="text-xs text-ink/70 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5" data-testid={`renewal-vendor-${i.id}`}>
                      <span className="inline-flex items-center gap-1 font-semibold text-ink"><Building2 className="h-3 w-3 text-ink/40" /> {i.vendor.company || "Vendor"}</span>
                      {i.vendor.contact_name && <span className="inline-flex items-center gap-1"><User className="h-3 w-3 text-ink/40" /> {i.vendor.contact_name}</span>}
                      {i.vendor.phone && <a href={`tel:${i.vendor.phone}`} className="inline-flex items-center gap-1 hover:text-ink"><Phone className="h-3 w-3 text-ink/40" /> {i.vendor.phone}</a>}
                      {i.vendor.email && <a href={`mailto:${i.vendor.email}`} className="inline-flex items-center gap-1 hover:text-ink"><Mail className="h-3 w-3 text-ink/40" /> {i.vendor.email}</a>}
                      {i.vendor.account_number && <span className="inline-flex items-center gap-1"><Hash className="h-3 w-3 text-ink/40" /> {i.vendor.account_number}</span>}
                    </div>
                  )}
                  {nextReminderText(i) && (
                    <div className="text-xs text-ink/60 mt-1 inline-flex items-center gap-1.5" data-testid={`renewal-next-reminder-${i.id}`}>
                      <BellRing className="h-3 w-3 text-ink/40" /> {nextReminderText(i)}
                      <span className="text-ink/40" data-testid={`renewal-cadence-${i.id}`}>· {[...(i.effective_lead_days || i.lead_days || [90, 60, 30, 7])].sort((a, b) => b - a).join("/")}d · overdue {overdueLabel(i.effective_overdue_every_days ?? 7)}{i.uses_default_reminders ? "" : " · custom"}</span>
                    </div>
                  )}
                  {i.notify && (
                    <div className={`text-xs mt-1 inline-flex items-center gap-1.5 ${i.notify.count ? "text-ink/60" : "text-amber-700"}`}
                      data-testid={`renewal-notify-${i.id}`}
                      title={i.notify.count ? i.notify.recipients.map((r) => `${r.name} · ${r.channels.map((c) => CHAN_WORD[c] || c).join(" + ")}`).join("\n") : "No one on the team will receive this — check owners and Notification settings."}>
                      <Users className="h-3 w-3 text-ink/40" /> {notifyLabel(i.notify)}
                    </div>
                  )}
                  {i.notes && <div className="text-xs text-ink/70 mt-1.5 whitespace-pre-wrap">{i.notes}</div>}
                  {i.cycles_completed > 0 && (
                    <button data-testid={`renewal-history-toggle-${i.id}`}
                      onClick={() => setOpenHistory((h) => ({ ...h, [i.id]: !h[i.id] }))}
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-muted-foreground hover:text-ink">
                      <History className="h-3.5 w-3.5" />
                      {i.cycles_completed} filed cycle{i.cycles_completed === 1 ? "" : "s"}
                    </button>
                  )}
                  {openHistory[i.id] && (
                    <div className="mt-2 space-y-1" data-testid={`renewal-history-${i.id}`}>
                      {[...(i.history || [])].reverse().map((h, ri) => {
                        const idx = (i.history || []).length - 1 - ri;
                        return (
                          <div key={idx} className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                            <FileCheck2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                            Due {h.was_due_on} · filed {h.completed_on} by {h.completed_by}
                            {h.note ? ` — ${h.note}` : ""}
                            {h.proof?.filename && (
                              <a data-testid={`renewal-proof-${i.id}-${idx}`}
                                href={`${API}/tenants/${tenantId}/renewals/${i.id}/proof/${idx}`}
                                target="_blank" rel="noreferrer"
                                className="font-bold text-redline-600 hover:text-redline-700 inline-flex items-center gap-1">
                                <Paperclip className="h-3 w-3" /> {h.proof.filename}
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`renewal-details-${i.id}`} onClick={() => setDetailId(i.id)} title="Details"
                    className="h-8 rounded-lg border border-sand-200 bg-white px-2.5 text-xs font-semibold text-ink/70 hover:text-ink hover:border-ink flex items-center gap-1.5 transition-colors">
                    <Eye className="h-3.5 w-3.5" /> Details
                  </button>
                {!readOnly && (
                  <>
                    <button data-testid={`renewal-complete-${i.id}`}
                      onClick={() => startComplete(i)}
                      className="rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-3.5 py-1.5 text-xs font-bold hover:bg-emerald-100 transition-colors flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Mark renewed
                    </button>
                    <button data-testid={`renewal-edit-${i.id}`} onClick={() => startEdit(i)}
                      className="h-8 w-8 rounded-lg hover:bg-sand-200 text-muted-foreground hover:text-ink flex items-center justify-center transition-colors">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`renewal-delete-${i.id}`} onClick={() => remove(i)}
                      className="h-8 w-8 rounded-lg hover:bg-rose-50 text-muted-foreground hover:text-rose-600 flex items-center justify-center transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
                </div>
              </div>

              {completing?.id === i.id && (
                <div className="mt-3 pt-3 border-t border-sand-200 space-y-3" data-testid="renewal-complete-panel">
                  <Field label="Attach the proof" hint="certificate, permit, receipt — optional but worth it">
                    <div className="flex flex-wrap items-center gap-2">
                      <button data-testid="renewal-proof-pick" onClick={() => proofRef.current?.click()}
                        className="rounded-full border border-sand-200 bg-white px-4 py-2 text-xs font-bold text-ink hover:border-ink transition-colors flex items-center gap-1.5">
                        <Paperclip className="h-4 w-4" /> {proofFile ? "Change file" : "Choose file"}
                      </button>
                      {proofFile && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-sand-100 px-3 py-1.5 text-xs font-semibold">
                          {proofFile.name}
                          <button data-testid="renewal-proof-clear" onClick={() => setProofFile(null)}
                            className="text-ink/40 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
                        </span>
                      )}
                      <input ref={proofRef} type="file" className="hidden"
                        onChange={(e) => { setProofFile(e.target.files?.[0] || null); e.target.value = ""; }} />
                    </div>
                  </Field>
                  <Field label="Note" hint="optional">
                    <input data-testid="renewal-complete-note" value={completeNote}
                      onChange={(e) => setCompleteNote(e.target.value)} className={inputCls}
                      placeholder="Passed on the first visit" />
                  </Field>
                  <div className="flex items-center gap-2">
                    <button data-testid="renewal-complete-confirm" disabled={busy} onClick={fileCycle}
                      className="rounded-full bg-emerald-600 text-white font-semibold px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-60">
                      {busy ? "Filing…" : "File it & schedule the next one"}
                    </button>
                    <button data-testid="renewal-complete-cancel" onClick={() => setCompleting(null)}
                      className="text-sm font-semibold text-muted-foreground hover:text-ink">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {pages > 1 && <div className="flex justify-end">{pager}</div>}
        </section>
        );
      })())}

      {/* starter suggestions — collapsed behind a chevron so the register isn't buried */}
      {!readOnly && (data.suggestions || []).length > 0 && (
        <section className="bg-white rounded-2xl border border-sand-200 p-5" data-testid="renewal-suggestions">
          <button type="button" data-testid="renewal-suggestions-toggle" aria-expanded={suggestOpen} onClick={() => setSuggestOpen((o) => !o)}
            className="w-full flex items-center gap-2 text-left">
            <Sparkles className="h-4.5 w-4.5 text-redline-500 shrink-0" strokeWidth={2} />
            <h2 className="font-heading font-bold text-ink">Commonly tracked</h2>
            <span className="text-xs text-muted-foreground" data-testid="renewal-suggestions-count">{data.suggestions.length} suggestion{data.suggestions.length === 1 ? "" : "s"}</span>
            <ChevronDown className={`h-4 w-4 text-ink/50 ml-auto transition-transform ${suggestOpen ? "rotate-180" : ""}`} />
          </button>
          {suggestOpen && (<>
          <p className="text-xs text-muted-foreground mb-3 mt-1">
            The obligations most associations carry. Add the ones that apply — you set the date — and dismiss the rest.
          </p>
          <div className="flex flex-wrap gap-2" data-testid="renewal-suggestions-list">
            {data.suggestions.map((s) => (
              <span key={s.key} className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-sand-50 pl-3.5 pr-1.5 py-1.5 text-xs"
                data-testid={`renewal-suggestion-${s.key}`}>
                <button onClick={() => accept(s)} data-testid={`renewal-suggestion-add-${s.key}`}
                  className="font-semibold text-ink hover:text-redline-600 transition-colors flex items-center gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> {s.title}
                </button>
                <button onClick={() => dismiss(s)} data-testid={`renewal-suggestion-dismiss-${s.key}`}
                  title={`Not for this ${V.lc("org")}`} className="text-ink/30 hover:text-rose-500 p-0.5">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
          </>)}
        </section>
      )}

      <RenewalDetailSheet item={detailItem} tenantId={tenantId} readOnly={readOnly} onClose={() => setDetailId(null)}
        onEdit={(i) => { setDetailId(null); startEdit(i); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        onComplete={(i) => { setDetailId(null); startComplete(i); setTimeout(() => document.querySelector(`[data-testid="renewal-${i.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50); }}
        onTest={(i) => sendTest(i)} />

      {readOnly && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="renewals-readonly-note">
          <AlertTriangle className="h-3.5 w-3.5" /> {V.many("board")} see the register read-only — your managers file the renewals.
        </p>
      )}
    </div>
  );
}
